import { wrapText, type TextNode } from '../../render/text';
import { last } from '../../render/utils/helper';
import { clamp } from '../../render/utils/math';
import { isTextSupported, splitGraphemes, WrapType } from '../../render/utils/wrap-text';
import { Provider } from '../provider';
import { TextInputController } from './text-input-controller';
import { signal } from '../../common-utils/signal';
import type { TextLayoutProvider, TextLayoutCacheItem } from '../text-layout';

const latinCharacterRegex = /[\p{Script=Latin}\p{M}_'’\u2010\u2011-]/u;

export class TextProvider extends Provider {
  /**
   * The start position of the selection, [line, column]
   */
  get anchorPosition() {
    return this.anchorPosition$.value;
  }

  anchorPosition$ = signal<readonly [number, number]>([0, 0]);

  /**
   * The end position of the selection, [line, column]
   */
  get focusPosition() {
    return this.focusPosition$.value;
  }

  focusPosition$ = signal<readonly [number, number]>([0, 0]);

  get isCollapsed() {
    return (
      this.anchorPosition[0] === this.focusPosition[0] &&
      this.anchorPosition[1] === this.focusPosition[1]
    );
  }

  /**
   * The normalized selection range, the first is always the earlier one.
   */
  get normalizedSelection(): [readonly [number, number], readonly [number, number]] {
    const focusAfterAnchor = this.isFocusBehindAnchor;

    return [
      focusAfterAnchor ? this.anchorPosition : this.focusPosition,
      focusAfterAnchor ? this.focusPosition : this.anchorPosition,
    ];
  }

  get isFocusBehindAnchor() {
    return (
      this.focusPosition[0] > this.anchorPosition[0] ||
      (this.focusPosition[0] === this.anchorPosition[0] &&
        this.focusPosition[1] > this.anchorPosition[1])
    );
  }

  /**
   * The currently edited text node
   */
  get currentNode() {
    return this.currentNode$.value;
  }

  currentNode$ = signal<TextNode | null>(null);

  /**
   * Input controller which encapsulates textarea + mask and emits normalized ops
   */
  inputController: TextInputController | null = null;

  /**
   * The undo stack for text editing
   **/
  undoStack: Array<{
    text: string;
    focusPosition: readonly [number, number];
    anchorPosition: readonly [number, number];
  }> = [];
  /**
   * The redo stack for text editing
   */
  redoStack: Array<{
    text: string;
    focusPosition: readonly [number, number];
    anchorPosition: readonly [number, number];
  }> = [];
  MAX_HISTORY: number = 500;

  /**
   * The following properties are deprecated, they will be deleted once
   * the refactoring is complete.
   */
  // editing flags/state
  isUpdating: boolean = false;
  isSelecting: boolean = false;

  constructor() {
    super();
  }

  private _swapAnchorAndFocus() {
    const oldAnchor = this.anchorPosition;
    this.anchorPosition$.set(this.focusPosition);
    this.focusPosition$.set(oldAnchor);
  }

  private _getGraphemeAt(position: readonly [number, number], direction: -1 | 1) {
    if (!this._layoutData) return null;

    const { lines } = this._layoutData;
    const [lineIdx, charIdx] = position;
    if (lineIdx < 0 || lineIdx >= lines.length) return '';
    const line = lines[lineIdx];
    return line[direction === 1 ? charIdx : charIdx - 1] ?? '';
  }

  getTextInRange(
    range: [readonly [number, number], readonly [number, number]],
    useGraphemes?: false
  ): string;
  getTextInRange(
    range: [readonly [number, number], readonly [number, number]],
    useGraphemes: true
  ): string[];
  getTextInRange(
    range: [readonly [number, number], readonly [number, number]],
    useGraphemes: boolean = false
  ): string | string[] {
    if (!this._layoutData || !this._layoutData.lines.length) return useGraphemes ? [] : '';

    const { lines, linesBreakType } = this._layoutData;
    let [start, end] = range;
    if (start[0] > end[0] || (start[0] === end[0] && start[1] > end[1])) {
      [start, end] = [end, start];
    }

    const sameLine = start[0] === end[0];
    let rangeText: string[] = sameLine
      ? (lines[start[0]]?.slice(start[1], end[1]) ?? [])
      : (lines[start[0]]?.slice(start[1], undefined) ?? []);

    if (!sameLine) {
      for (let i = start[0] + 1; i <= end[0]; i++) {
        if (linesBreakType[i] === WrapType.HARD) {
          rangeText.push('\n');
        }
        if (!lines[i]) {
          break;
        }
        rangeText = rangeText.concat(i === end[0] ? lines[i].slice(0, end[1]) : lines[i]);
      }
    }

    return useGraphemes ? rangeText : rangeText.join('');
  }

  positionToTextIndex(position: readonly [number, number]): number {
    if (!this._layoutData) return 0;

    const { lines, linesBreakType } = this._layoutData;

    let index = 0;
    for (let i = 0; i < lines.length; ++i) {
      const line = lines[i];
      if (i > 0 && linesBreakType[i] === WrapType.HARD) {
        index += 1;
      }
      if (i < position[0]) {
        index += line.length;
      } else if (i === position[0]) {
        index += Math.min(position[1], line.length);
        break;
      }
    }

    return index;
  }

  private _textIndexToPosition(index: number): readonly [number, number] {
    if (!this._layoutData) return [0, 0];
    const { lines, linesBreakType } = this._layoutData;

    const position: [number, number] = [0, 0];

    let i = 0;
    for (; i < lines.length; ++i) {
      const line = lines[i];

      if (i > 0 && linesBreakType[i] === WrapType.HARD) {
        index -= 1;
      }

      if (index > line.length) {
        index -= line.length;
      } else {
        break;
      }
    }

    position[0] = Math.min(i, Math.max(0, lines.length - 1));
    position[1] = Math.max(0, index);

    return position;
  }

  attach(stageContext) {
    this.renderContext = stageContext;
    if (!this.inputController) {
      this.inputController = new TextInputController(this);
    }
  }

  activate() {
    const selectedIds$ = this.renderContext.providers.selection.selectedIds$;
    this.diposable.push(
      selectedIds$.subscribe(() => {
        if (this.currentNode && !selectedIds$.peek().has(this.currentNode.id)) {
          this.cancel();
        }
      })
    );
  }

  get debug() {
    return this.renderContext?.debug || false;
  }

  dispose() {
    super.dispose();
    this.cancel();
    this.inputController?.destroy();
    this.inputController = null;
  }

  create(node) {
    if (this.isEditing(node)) return;
    this.currentNode$.set(node);

    const layoutData = this._layoutData;
    if (!layoutData) {
      return;
    }

    this.anchorPosition$.set([0, 0]);
    this.focusPosition$.set([
      layoutData.lines.length - 1,
      layoutData.lines[layoutData.lines.length - 1].length,
    ]);
    // 激活输入控制器（聚焦隐藏输入、显示遮罩并同步布局）
    this.inputController?.activate(node);

    // 重置历史并记录初始快照
    this.undoStack = [];
    this.redoStack = [];
    this._pushUndoSnapshot();

    // 提示渲染系统切换为编辑模式
    this.update();

    // 同步遮罩布局
    this.inputController?.updateInputElmPosition();
  }

  debouncedUpdateText(nodeId, text) {
    this.isUpdating = true;
    this.renderContext.api.updateNodeText(nodeId, { text });
    this._forceTextLayout();
    this.isUpdating = false;
  }

  getRenderedTextContent() {
    if (!this.currentNode) return '';
    return this.currentNode.text;
  }

  getAllSelection(_node): [number, number] {
    const textLayout = this._layoutData;
    if (!textLayout) return [0, 0];

    const { lines } = textLayout;

    return [
      this.positionToTextIndex([0, 0]),
      this.positionToTextIndex([lines.length - 1, lines[lines.length - 1].length]),
    ];
  }

  /**
   * Get the current selection in terms of text index.
   * @returns
   */
  getCurrentSelection(): [number, number] {
    const [start, end] = this.normalizedSelection;
    return [this.positionToTextIndex(start), this.positionToTextIndex(end)];
  }

  getCurrentSelectionText() {
    if (!this.currentNode) return '';

    const [start, end] = this.normalizedSelection;
    return this.getTextInRange([start, end]);
  }

  tryCancel(selectedNodes) {
    if (!this.currentNode) return;
    const isNotInSelection = !selectedNodes.includes(this.currentNode);
    if (isNotInSelection) {
      this.cancel();
    }
  }

  cancel() {
    this.inputController?.deactivate();
    this.isUpdating = false;
    this.currentNode$.set(null);
    const container = this.renderContext.container;

    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    container && container.focus();
  }

  /**
   * Get TextLayoutProvider from stageContext
   */
  private get _textLayoutProvider(): TextLayoutProvider {
    const provider = this.renderContext.providers?.textLayout;
    if (!provider) {
      throw new Error('TextLayoutProvider is required');
    }
    return provider;
  }

  private get _layoutData(): TextLayoutCacheItem['textLayout'] | null {
    if (!this.currentNode) return null;
    return this._textLayoutProvider.getTextLayout(this.currentNode.id);
  }

  private get _graphemes() {
    if (!this.currentNode) return null;
    return this._textLayoutProvider.getGraphemeLayout(this.currentNode.id);
  }

  /**
   * @returns false if no valid layout, the positions are reset to [0, 0]
   */
  private _updateFocusPosition(move: {
    offset?: number | 'start' | 'end' | 'hardstart' | 'hardend';
    line?: number | 'start' | 'end';
    collapsed?: boolean;
  }): boolean {
    if (!this._layoutData || !this._layoutData.lines.length) {
      this.anchorPosition$.set([0, 0]);
      this.focusPosition$.set([0, 0]);
      return false;
    }

    const { offset, line, collapsed } = move;
    const textLayout = this._layoutData;
    const { lines, linesBreakType } = this._layoutData;
    const candidateFocusPos = [...this.focusPosition];

    if (line) {
      if (typeof line === 'number') {
        candidateFocusPos[0] = clamp(candidateFocusPos[0] + line, 0, textLayout.lines.length - 1);
      } else {
        candidateFocusPos[0] = line === 'start' ? 0 : lines.length - 1;
      }
    }

    if (offset) {
      let lineIdx = clamp(candidateFocusPos[0], 0, lines.length - 1);
      let line = lines[lineIdx];

      if (typeof offset === 'number') {
        let candidate = candidateFocusPos[1] + offset;

        if (candidate < 0) {
          if (lineIdx > 0) {
            candidateFocusPos[0] = lineIdx - 1;
            const prevLine = textLayout.lines[candidateFocusPos[0]];
            candidate = prevLine.length;
          } else {
            candidate = 0;
          }
        } else if (candidate > line.length) {
          if (lineIdx < textLayout.lines.length - 1) {
            candidateFocusPos[0] = lineIdx + 1;
            candidate = 0;
          } else {
            candidate = line.length;
          }
        }
        candidateFocusPos[1] = candidate;
      } else {
        if (offset.startsWith('hard')) {
          for (let i = lineIdx; i >= 0; --i) {
            if (linesBreakType[i] === WrapType.HARD) {
              line = lines[i];
              lineIdx = i;
              candidateFocusPos[0] = lineIdx;
              break;
            }
          }
        }

        candidateFocusPos[1] = offset.includes('start') ? 0 : line.length;
      }
    }

    this.focusPosition$.set(candidateFocusPos as [number, number]);

    if (collapsed) {
      this.anchorPosition$.set([...this.focusPosition]);
    }

    return true;
  }

  /**
   * Normally the text layout is updated during rendering phase,
   * but when entering editing mode, we need to ensure the layout is up-to-date with
   * the current text content.
   *
   * It won't effect the actual rendering because the rendering phase will
   * skip the layout if it is already up-to-date.
   */
  private _forceTextLayout() {
    if (!this.currentNode) {
      return;
    }

    const text = this.currentNode.text || '';
    const textLayout = wrapText(this.currentNode, text);
    const fontLoaded = isTextSupported(this.currentNode, text, this.renderContext);

    // Update TextLayoutProvider
    this._textLayoutProvider.setTextLayout(this.currentNode.id, textLayout, fontLoaded);
  }

  /**
   * Replace the currently selected text with the given text.
   * @param insertedText The text to insert
   */
  private _replaceSelectedText(
    insertedText: string,
    option?: {
      collapsed?: boolean;
      addHistory?: boolean;
    }
  ) {
    const { addHistory = true, collapsed = true } = option ?? {};
    if (!this.currentNode || !this._layoutData) return;

    if (addHistory) {
      this._pushUndoSnapshot();
    }

    const { lines } = this._layoutData;
    const [start, end] = this.normalizedSelection;
    const beforeTextGraphemes = this.getTextInRange([[0, 0], start], true);
    const afterTextGraphemes = this.getTextInRange(
      [end, [Math.max(0, lines.length - 1), last(lines)?.length ?? 0]],
      true
    );
    const newText = beforeTextGraphemes.join('') + insertedText + afterTextGraphemes.join('');

    let newFocusPosInTextIdx = this.positionToTextIndex(start);
    if (insertedText) {
      const insertedLines = insertedText.split('\n');

      if (insertedLines.length > 1) {
        insertedLines.forEach((insrtLin, idx) => {
          const graphemes = splitGraphemes(insrtLin);

          newFocusPosInTextIdx += graphemes.length;
          if (idx < insertedLines.length - 1) {
            newFocusPosInTextIdx += 1;
          }
        });
      } else {
        newFocusPosInTextIdx += splitGraphemes(insertedText).length;
      }
    }

    this.debouncedUpdateText(this.currentNode?.id, newText);

    this.focusPosition$.set(this._textIndexToPosition(newFocusPosInTextIdx));
    if (collapsed) {
      this.anchorPosition$.set([...this.focusPosition]);
    }
    this.renderContext?.api?.markNeedRender();
  }

  replaceSelectedText(
    insertedText: string,
    option?: {
      collapsed?: boolean;
      addHistory?: boolean;
    }
  ) {
    this._replaceSelectedText(insertedText, option);
  }

  moveCursorToDocumentStart(expand = false) {
    this._updateFocusPosition({
      line: 'start',
      offset: 'start',
      collapsed: !expand,
    });
    this.renderContext?.api?.markNeedRender();
  }

  moveCursorToDocumentEnd(expand = false) {
    this._updateFocusPosition({
      line: 'end',
      offset: 'end',
      collapsed: !expand,
    });
    this.renderContext?.api?.markNeedRender();
  }

  isEditing(node) {
    if (!node) return false;
    return this.currentNode && node.id === this.currentNode?.id;
  }

  get hasEditing() {
    return !!this.currentNode;
  }

  update() {
    if (!this.currentNode || !this.renderContext.api.getNode(this.currentNode.id)) {
      return;
    }

    this.inputController?.updateInputElmPosition();
  }

  /**
   * @param stagePoint The point in stage coordinates
   * @returns
   */
  beginSelectionFromPoint(stagePoint: { x: number; y: number }) {
    if (!this.currentNode) return;
    const node = this.renderContext.api.getNode(this.currentNode.id);
    if (!node) return;

    const hitPosition = this._getGraphemePosFromPoint(stagePoint);
    if (!hitPosition) return;

    this.focusPosition$.set(hitPosition);
    this.anchorPosition$.set(hitPosition);
    this.isSelecting = true;

    this.renderContext?.api?.markNeedRender();
  }

  /**
   * @param stagePoint The point in stage coordinates
   * @returns
   */
  updateSelectionToPoint(stagePoint: { x: number; y: number }) {
    if (!this.currentNode || !this.isSelecting) return;

    const node = this.renderContext.api.getNode(this.currentNode.id);
    if (!node) return;

    const hitPosition = this._getGraphemePosFromPoint(stagePoint);
    if (!hitPosition) return;

    this.focusPosition$.set(hitPosition);
    this.isSelecting = true;
    this.renderContext?.api?.markNeedRender();
  }

  endSelection() {
    this.isSelecting = false;
    this.renderContext?.api?.markNeedRender();
  }

  insertText(insertedText: string) {
    this._replaceSelectedText(insertedText);
  }

  /**
   * Delete the text in the given direction. But if there is a selection, it will delete the selection regardless of direction.
   * @param direction -1 for backspace, +1 for delete, 0 for delete selection
   * @returns
   */
  deleteText(direction: -1 | 1 | 0) {
    this._pushUndoSnapshot();
    if (!this.isCollapsed) {
      this._replaceSelectedText('', { addHistory: false });
    } else {
      this._updateFocusPosition({
        offset: direction,
      });
      this._replaceSelectedText('', { addHistory: false });
    }
  }

  /**
   * Delete the continuous sequence of latin characters before or after the cursor.
   * @param direction
   * @returns
   */
  deleteWord(direction: -1 | 1) {
    if (!this.isCollapsed) {
      this.deleteText(0);
    } else {
      if (!this._layoutData) return;

      this.moveCursorToWordBoundary(direction, true);
      this.deleteText(0);
    }
  }

  collapse(target: 'anchor' | 'focus' = 'focus') {
    if (target === 'focus') {
      this.anchorPosition$.set([...this.focusPosition]);
    } else {
      this.focusPosition$.set([...this.anchorPosition]);
    }
  }

  /**
   * Move cursor by one position
   * @param delta -1 for left, +1 for right
   * @param extend
   * @returns
   */
  moveCursor(delta: number, expand = false) {
    this._updateFocusPosition({
      offset: delta,
      collapsed: !expand,
    });
    this.renderContext?.api?.markNeedRender();
  }

  /**
   * @param direction 1 for right, -1 for left
   * @param expand whether to expand selection
   * @returns
   */
  moveCursorToWordBoundary(direction: 1 | -1, expand = false) {
    if (!this._layoutData) return;

    const layoutData = this._layoutData;
    let matched = false;
    let lastGrapheme = '';

    do {
      this._updateFocusPosition({
        offset: direction,
        collapsed: !expand,
      });

      lastGrapheme = this._getGraphemeAt(this.focusPosition, direction === 1 ? -1 : 1) ?? '';
      matched = latinCharacterRegex.test(lastGrapheme);
      if (lastGrapheme && !matched) break;
    } while (
      direction === -1
        ? this.focusPosition[0] > 0 || this.focusPosition[1] > 0
        : this.focusPosition[0] < layoutData.lines.length - 1 ||
          this.focusPosition[1] < layoutData.lines[layoutData.lines.length - 1].length
    );

    if (lastGrapheme && !matched) {
      this._updateFocusPosition({
        offset: direction === -1 ? 1 : -1,
        collapsed: !expand,
      });
    }

    this.renderContext?.api?.markNeedRender();
  }

  /**
   * @param direction 1 for down, -1 for up
   * @param expand whether to expand selection
   * @returns
   */
  moveCursorVertical(direction: 1 | -1, expand = false) {
    this._updateFocusPosition({
      line: direction,
      collapsed: !expand,
    });
    this.renderContext?.api?.markNeedRender();
  }

  moveCursorToLineStart(expand = false) {
    this._updateFocusPosition({
      offset: 'hardstart',
      collapsed: !expand,
    });
    this.renderContext?.api?.markNeedRender();
  }

  moveCursorToLineEnd(expand = false) {
    this._updateFocusPosition({
      offset: 'hardend',
      collapsed: !expand,
    });
    this.renderContext?.api?.markNeedRender();
  }

  focus() {
    if (this.currentNode) this.inputController?.activate(this.currentNode);
  }

  selectAll() {
    this.anchorPosition$.set([0, 0]);
    this._updateFocusPosition({
      line: 'end',
      offset: 'end',
    });
    this.renderContext?.api?.markNeedRender();
  }

  copy() {
    if (this.isCollapsed || !(typeof navigator !== 'undefined' && navigator.clipboard)) {
      return;
    }

    const selectedText = this.getTextInRange(this.normalizedSelection);

    navigator.clipboard.writeText(selectedText).catch(() => {});
  }

  cut() {
    if (this.isCollapsed) return;
    this.copy();
    this.deleteText(0);
  }

  paste() {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard
        .readText()
        .then((text) => {
          if (text) this.insertText(text);
        })
        .catch(() => {});
    }
  }

  private _getSnapshot(): {
    text: string;
    focusPosition: readonly [number, number];
    anchorPosition: readonly [number, number];
  } {
    return {
      text: this.currentNode?.text || '',
      focusPosition: this.focusPosition,
      anchorPosition: this.anchorPosition,
    };
  }

  private _applySnapshot(snapshot) {
    if (!this.currentNode) return;
    this.focusPosition$.set(snapshot.focusPosition);
    this.anchorPosition$.set(snapshot.anchorPosition);
    this.debouncedUpdateText(this.currentNode?.id, snapshot.text);
    this.renderContext?.api?.markNeedRender();
  }

  private _pushUndoSnapshot() {
    const snap = this._getSnapshot();
    this.undoStack.push(snap);
    if (this.undoStack.length > this.MAX_HISTORY) this.undoStack.shift();
    this.redoStack = [];
  }

  undo() {
    if (!this.currentNode) return;
    if (this.undoStack.length === 0) return;
    const current = this._getSnapshot();
    const prev = this.undoStack.pop();
    this.redoStack.push(current);
    if (this.redoStack.length > this.MAX_HISTORY) this.redoStack.shift();
    this._applySnapshot(prev);
  }

  redo() {
    if (!this.currentNode) return;
    if (this.redoStack.length === 0) return;
    const current = this._getSnapshot();
    const next = this.redoStack.pop();
    this.undoStack.push(current);
    if (this.undoStack.length > this.MAX_HISTORY) this.undoStack.shift();
    this._applySnapshot(next);
  }

  /**
   * Get the grapheme at the specified point.
   * @param point The absolute point in stage coordinates.
   * @returns the selection of the hit grapheme, or null if none.
   */
  private _getGraphemePosFromPoint(point: {
    x: number;
    y: number;
  }): readonly [number, number] | null {
    if (!this._graphemes || !this._layoutData || !this.currentNode) return null;
    const graphemes = this._graphemes;
    const { fontSettings } = this._layoutData;
    const lineHeight = fontSettings.lineHeight * this.currentNode.fontSize;
    const halfLineHeight = lineHeight / 2;

    const startY = this.currentNode.y;

    for (let i = 0; i < graphemes.length; i++) {
      if (!graphemes[i].length) {
        if (point.y >= startY + i * lineHeight && point.y <= startY + i * lineHeight + lineHeight) {
          return [i, 0] as readonly [number, number];
        }

        continue;
      }
      const curLineGraphemes = graphemes[i];
      const firstG = curLineGraphemes[0];

      const region = this.renderContext.api.getNodeRegion(this.currentNode);
      if (!region) return null;
      const localPoint = region.toLocal(point);
      const { x: rawX, y: rawY } = this.currentNode;

      if (
        localPoint.y >= firstG.y - halfLineHeight - rawY &&
        localPoint.y <= firstG.y - halfLineHeight - rawY + firstG.height
      ) {
        const lastG = curLineGraphemes[curLineGraphemes.length - 1];
        if (localPoint.x < firstG.x - rawX) {
          return [i, 0] as readonly [number, number];
        } else if (localPoint.x > lastG.x + lastG.width - rawX) {
          return [i, curLineGraphemes.length] as readonly [number, number];
        }
        for (let j = 0; j < curLineGraphemes.length; j++) {
          const g = curLineGraphemes[j];
          if (localPoint.x >= g.x - rawX && localPoint.x <= g.x + g.width - rawX) {
            return [i, localPoint.x > g.x - rawX + g.width / 2 ? j + 1 : j] as readonly [
              number,
              number,
            ];
          }
        }
      }
    }

    return null;
  }

  onDoubleClick(e: MouseEvent) {
    if (!this.currentNode) return;
    e.stopPropagation();
    e.preventDefault();

    // 获取双击位置对应的字符索引
    const point = this.renderContext?.interactive?.eventToPoint?.(e);
    if (!point) return;

    const node = this.renderContext.api.getNode(this.currentNode.id);
    if (!node) return;

    const hitIndex = this._getGraphemePosFromPoint(point);
    if (!hitIndex) return;

    this.focusPosition$.set(hitIndex);
    this.anchorPosition$.set(hitIndex);

    this.moveCursorToWordBoundary(-1, true);
    this._swapAnchorAndFocus();
    this.moveCursorToWordBoundary(1, true);

    this.renderContext?.api?.markNeedRender();
  }

  // Handle controller's doubleClick with an already-computed point
  private _doubleClickAtPoint(point: { x: number; y: number }) {
    if (!this.currentNode) return;
    const node = this.renderContext.api.getNode(this.currentNode.id);
    if (!node) return;
    const hitIndex = this._getGraphemePosFromPoint(point);
    if (!hitIndex) return;
    this.focusPosition$.set(hitIndex);
    this.anchorPosition$.set(hitIndex);
    this.moveCursorToWordBoundary(-1, true);
    this._swapAnchorAndFocus();
    this.moveCursorToWordBoundary(1, true);
    this.renderContext?.api?.markNeedRender();
  }

  // public wrapper for controller use
  public doubleClickAtPoint(point: { x: number; y: number }) {
    this._doubleClickAtPoint(point);
  }
}
